import { Component, OnInit, OnChanges, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { FilterState, StockoutRow, MachineHeatmapItem, AnalyticsHttpService, AnalyticsExportService, formatProductName } from 'app/core/analytics';

@Component({
  selector: 'kt-stockouts-tab',
  templateUrl: './stockouts-tab.component.html',
  styleUrls: [
    '../../analytics.component.scss',
    './stockouts-tab.component.scss'
  ]
})
export class StockoutsTabComponent implements OnInit, OnChanges {
  @Input() filters!: FilterState;
  @Input() drinkOptions?: Array<{ value: string | number; label: string }>;
  @Input() productOptions?: Array<{ value: string | number; label: string }>;
  @Input() distributorOptions?: Array<{ value: string | number; label: string }>;
  @Input() marketOptions?: Array<{ value: string | number; label: string }>;
  @Input() clientOptions?: Array<{ value: string | number; label: string }>;
  @Input() campusOptions?: Array<{ value: string | number; label: string }>;
  @Input() machineOptions?: Array<{ value: string | number; label: string }>;
  @Output() machineFilterChange = new EventEmitter<string[]>();

  public stockoutRows: StockoutRow[] = [];
  public machineHeatmap: MachineHeatmapItem[] = [];
  public isLoadingStockoutRisk = false;
  public isLoadingStockoutHeatmap = false;

  constructor(
    private analyticsHttpService: AnalyticsHttpService,
    private analyticsExportService: AnalyticsExportService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadStockoutRiskData();
    this.loadStockoutHeatmapData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change (but not on initial load)
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.loadStockoutRiskData();
      this.loadStockoutHeatmapData();
    }
  }

  /**
   * Build query parameters from current filters
   * Only includes hierarchical filter values
   */
  private buildQueryParams(): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    // Add hierarchical filters (only if not empty)
    if (this.filters.distributors.length > 0) {
      params.distributors = this.filters.distributors.join(',');
    }
    if (this.filters.markets.length > 0) {
      params.markets = this.filters.markets.join(',');
    }
    if (this.filters.clients.length > 0) {
      params.clients = this.filters.clients.join(',');
    }
    if (this.filters.campuses.length > 0) {
      params.campuses = this.filters.campuses.join(',');
    }
    if (this.filters.machines.length > 0) {
      params.machines = this.filters.machines.join(',');
    }

    return params;
  }

  /**
   * Load stockout risk data from API
   */
  loadStockoutRiskData(): void {
    if (this.isLoadingStockoutRisk) {
      return; // Prevent multiple simultaneous requests
    }

    // Clear existing data first
    this.stockoutRows = [];

    this.isLoadingStockoutRisk = true;
    const queryParams = this.buildQueryParams();

    this.analyticsHttpService.getStockoutRisk(queryParams).subscribe(
      (response: unknown) => {
        this.isLoadingStockoutRisk = false;
        this.handleStockoutRiskResponse(response);
      },
      (error: unknown) => {
        this.isLoadingStockoutRisk = false;
        console.error('Error loading stockout risk data:', error);
        // On error, clear the data
        this.stockoutRows = [];
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Handle stockout risk API response
   */
  private handleStockoutRiskResponse(response: unknown): void {
    if (!response || typeof response !== 'object') {
      console.error('Invalid stockout risk response structure:', response);
      this.stockoutRows = [];
      this.cdr.detectChanges();
      return;
    }

    const responseObj = response as Record<string, unknown>;

    // Check if response is wrapped in a data property
    const data = (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data))
      ? responseObj.data as Record<string, unknown>
      : responseObj;

    const rows = (data.rows as Array<Record<string, unknown>>) || [];

    // Transform rows to StockoutRow format
    this.stockoutRows = rows.map(row => ({
      machine: (row.machine as string) || '',
      product: formatProductName((row.product as string) || ''),
      inventory: Math.round(((row.inventory as number) || 0) * 100) / 100,
      avgDaily: Math.round(((row.avgDaily as number) || 0) * 100) / 100,
      daysLeft: Math.round(((row.daysLeft as number) || 0) * 100) / 100,
      risk: (row.risk as 'low' | 'medium' | 'high') || 'low'
    }));

    this.cdr.detectChanges();
  }

  /**
   * Load stockout heatmap data from API
   */
  loadStockoutHeatmapData(): void {
    if (this.isLoadingStockoutHeatmap) {
      return; // Prevent multiple simultaneous requests
    }

    // Clear existing data first
    this.machineHeatmap = [];

    this.isLoadingStockoutHeatmap = true;
    const queryParams = this.buildQueryParams();

    this.analyticsHttpService.getStockoutHeatmap(queryParams).subscribe(
      (response: unknown) => {
        this.isLoadingStockoutHeatmap = false;
        this.handleStockoutHeatmapResponse(response);
      },
      (error: unknown) => {
        this.isLoadingStockoutHeatmap = false;
        console.error('Error loading stockout heatmap data:', error);
        // On error, clear the data
        this.machineHeatmap = [];
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Handle stockout heatmap API response
   */
  private handleStockoutHeatmapResponse(response: unknown): void {
    if (!response || typeof response !== 'object') {
      console.error('Invalid stockout heatmap response structure:', response);
      this.machineHeatmap = [];
      this.cdr.detectChanges();
      return;
    }

    const responseObj = response as Record<string, unknown>;

    // Check if response is wrapped in a data property
    const data = (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data))
      ? responseObj.data as Record<string, unknown>
      : responseObj;

    const heatmap = (data.heatmap as Array<Record<string, unknown>>) || [];

    // Transform heatmap to MachineHeatmapItem format
    this.machineHeatmap = heatmap.map(item => ({
      machine: (item.machine as string) || '',
      minDays: Math.round(((item.minDays as number) || 0) * 100) / 100,
      risk: (item.risk as 'low' | 'medium' | 'high') || 'low',
      product: (item.product as string) || ''
    }));

    this.cdr.detectChanges();
  }

  /**
   * Toggle machine filter when clicking on heatmap item
   */
  toggleMachineFilter(machine: string): void {
    const index = this.filters.machines.indexOf(machine);
    let updatedMachines: string[];

    if (index > -1) {
      updatedMachines = this.filters.machines.filter(m => m !== machine);
    } else {
      updatedMachines = this.filters.machines.concat([machine]);
    }

    this.machineFilterChange.emit(updatedMachines);
  }

  hasNoData(): boolean {
    return this.stockoutRows.length === 0;
  }

  hasNoHeatmapData(): boolean {
    return this.machineHeatmap.length === 0;
  }

  /**
   * Check if data is available for export
   */
  hasData(): boolean {
    return this.stockoutRows.length > 0;
  }

  /**
   * Export stockouts table to Excel
   */
  exportTable(): void {
    try {
      // Convert stockout rows to export format (risk is already text format)
      const exportRows = this.stockoutRows.map(row => ({
        machine: row.machine,
        product: row.product,
        inventory: row.inventory,
        daysLeft: row.daysLeft,
        risk: row.risk // Already 'low' | 'medium' | 'high'
      }));

      const exportData = {
        rows: exportRows
      };

      this.analyticsExportService.exportStockoutsTable(
        exportData,
        this.filters,
        'Stock-outs',
        this.drinkOptions,
        this.productOptions,
        this.distributorOptions,
        this.marketOptions,
        this.clientOptions,
        this.campusOptions,
        this.machineOptions
      );
    } catch (error) {
      console.error('Error exporting stockouts table:', error);
      throw error;
    }
  }
}

